import React, { Fragment, useEffect, useState } from 'react';
import { Flex, Box, Button, Heading, Text } from 'rebass';
import { withWizard } from 'react-albus';
import { compose, Query, withApollo } from 'react-apollo';
import { withToastManager } from 'react-toast-notifications';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import check from '@iconify/icons-entypo/check';
import Dropzone from 'react-dropzone';
import { isMobile } from 'react-device-detect';
import PhotoTaker from '../asyncIntake/PhotoTaker';
import gql from 'graphql-tag';
import { Input } from '../TextInput';
import { MaskInput2 } from '../MaskInput';
import moment from 'moment';
import PleaseWait from '../PleaseWait';
import { GET_LABS_LIST, LABS } from '../../graphql';
import { sleep } from '../../utils/sleep';
import ConfirmModal from '../ConfirmModal';
import SpinLoader from '../SpinLoader';

const THRESHOLD_LAB_VALIDITY_DAYS = -120;

const ModalInner = styled(Box)`
  max-width: 600px;
  width: 100%;
`;
const CheckboxWrap = styled(Flex)`
  border-color: rgba(0, 0, 0, 0.19);
  border-width: 2px;
  border-radius: 3px;
  border-style: solid;
  transition: all var(0.3s) linear;
  overflow: hidden;
  height: 20px;
  width: 20px;
`;
const LabToUpload = styled(Flex)`
  background: #ffffff;
  border: 1px solid rgba(53, 78, 121, 0.5);
  border-radius: 10px;
  padding: 12px;
  position: relative;
  flex: 1;
`;

const Title = styled(Text)`
  font-weight: 400;
  font-size: 20px;
  line-height: 27px;
`;

const Description = styled(Text)`
  font-weight: 300;
  font-size: 14px;
  line-height: 21px;
  text-align: left;
`;

const EmptyStateFlexWrapper = styled(Flex)`
  cursor: pointer;
  &:focus {
    outline: 0 !important;
    box-shadow: none !important;
  }
  &:focus:not(.focus-visible) {
    outline: 0 !important;
    box-shadow: none !important;
  }
`;

const FakeLink = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  text-decoration-line: underline;
  cursor: pointer;
  color: #44a5ff;
`;
// flexDirection={'column'} style={{ maxWidth: '500px', marginTop: '16px', background: isMobile ? '' : 'white' }}
const ChecksContainer = styled(Flex)`
  flex-direction: column;
  max-width: 500px;
  margin-top: 16px;
  background: transparent;

  @media screen and (min-width: 52em) {
    max-width: 100%;
    width: 100%;
    background: white;
    padding: 24px;
    border-radius: 10px;
  }
`;

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const qryParseDocumentAi = gql`
  query parseLabDocumentValuesExtended($document: String!) {
    parseLabDocumentValuesExtended(document: $document) {
      biomarkers
    }
  }
`;

const convertShortAnalyteNameToLong = (short) => {
  switch (short) {
    case 'fT3':
      return 'Free T3';
    case 'fT4':
      return 'Free T4';
    case 'TSH':
      return 'TSH';
    case 'TPO':
      return 'Thyroid Peroxidase Antibodies (TPO)';
    case 'rT3':
      return 'Reverse T3';
    case 'vitaminD':
      return 'Vitamin D';

    default:
      return (short || '').replace(/_/g, ' ');
  }
};

const PictureMiniature = ({ file, onFileRemove, onConfirm, client }) => {
  const [collectionDate, setCollectionDate] = useState();
  const [errorDate, setErrorDate] = useState();
  const [parsedData, setParsedData] = useState();
  const [parsedUnconfidentData, setParsedUnconfidentData] = useState();
  const [parsedConfidentData, setParsedConfidentData] = useState();
  const [isParsing, setIsParsing] = useState(true);

  useEffect(
    () => {
      checkDate();
    },
    [collectionDate],
  );

  useEffect(() => {
    setIsParsing(true);
    if (file && client) {
      const getData = async () => {
        const { data } = await client.query({
          query: qryParseDocumentAi,
          fetchPolicy: 'network-only',
          variables: {
            document: file.data,
          },
        });

        if (data && data.parseLabDocumentValuesExtended && data.parseLabDocumentValuesExtended.biomarkers) {
          // console.log({data})
          const parsed = JSON.parse(data.parseLabDocumentValuesExtended.biomarkers);
          const confident = Object.fromEntries(Object.entries(parsed).filter(([key, value]) => key !== 'error' && key !== '__typename' && (!!value.confidence_score && value.confidence_score >= 90)));

          const unconfident = Object.fromEntries(Object.entries(parsed).filter(([key, value]) => key !== 'error' && key !== '__typename' && (!value.confidence_score || value.confidence_score < 90)));

          setParsedData(parsed);
          setParsedConfidentData(confident);
          setParsedUnconfidentData(unconfident);
          // console.log({ parsed });
          if (Object.keys(parsed).length > 0) {
            let collectionD = (parsed.fT3 || {}).collectionDate;

            // console.log({ collectionD });
            if (!collectionD) {
              const firstCollection = Object.values(parsed).find((item) => item.collectionDate !== undefined);
              console.log({ firstCollection });
              if (firstCollection) {
                collectionD = firstCollection.collectionDate;
              }
            }
            // console.log({ collectionD2: collectionD });
            setCollectionDate(collectionD);
          }
        }
        setIsParsing(false);
      };
      getData();
    }
  }, []);

  const checkDate = () => {
    if (!collectionDate || collectionDate.indexOf('_') > -1) {
      setErrorDate('Required');
      return false;
    }
    try {
      const d = moment(collectionDate, 'MM/DD/YYYY');
      if (d.isAfter(moment(), 'day')) {
        setErrorDate('Date must be in the past');
        return false;
      }
      setErrorDate(null);
      return true;
    } catch (error) {
      setErrorDate('Invalid date');
      return false;
    }
  };

  // Handle input change
  const handleChangeAnalyteValue = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      value: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  // Handle input change
  const handleChangeAnalyteUnits = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      units: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  // Handle input change
  const handleChangeUnconfidentAnalyteValue = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      value: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedUnconfidentData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };
  const handleChangeConfidentAnalyteEditStatus = (name) => {
    const prev = parsedData[name].isEditing;
    let newVal = {
      ...parsedData[name],
      isEditing: !prev,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  // Handle input change
  const handleChangeUnconfidentAnalyteUnits = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      units: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedUnconfidentData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };
  // Handle input change
  const handleChangeUnconfidentAnalyteCheck = (name) => {
    const prev = parsedData[name].isVerified;
    let newVal = {
      ...parsedData[name],
      isVerified: !prev,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedUnconfidentData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  return (
    <Flex flex={1} flexDirection="column" style={{ zIndex: 1, maxWidth: 'max(50%, 350px)', alignSelf: 'center' }}>
      <Box mt="0" mb={4}>
        <Heading textAlign="center">Please review your lab values</Heading>
      </Box>
      <Flex
        flexDirection="row"
        flexWrap="wrap"
        style={{
          width: 'calc(100vw / 2)',
          maxWidth: '400px',
          borderRadius: '20px',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <img
          src={(file || {}).data}
          alt="No Preview Available"
          width="100%"
          style={{
            maxHeight: '300px',
            borderRadius: '20px',
            objectFit: 'contain',
          }}
        />
        <Button onClick={onFileRemove} variant="white" style={{ position: 'absolute', bottom: '6px', right: '6px' }}>
          Change
        </Button>
      </Flex>
      <Box mt={3} mb={3}>
        <Title textAlign="left">Ensure that...</Title>
      </Box>
      <Description>
        <span>
          <Icon
            icon={check}
            style={{
              color: 'rgb(53, 78, 121)',
              fontSize: '18px',
              alignSelf: 'center',
            }}
          />
        </span>{' '}
        Your photo is clear and easy to read
      </Description>
      <Description mb={3}>
        <span>
          <Icon
            icon={check}
            style={{
              color: 'rgb(53, 78, 121)',
              fontSize: '18px',
              alignSelf: 'center',
            }}
          />
        </span>{' '}
        All lab values are visible and not cut off
      </Description>
      {!!parsedData &&
        Object.keys(parsedData).filter((key) => key !== 'error' && key !== '__typename').length > 0 && (
          <Flex style={{ gap: '16px' }} flexDirection={'column'}>
            <Flex flexDirection={'column'} mb={2}>
              <Text fontSize={'14px'} textAlign="left" mb={2}>
                We’ve automatically extracted your lab values using AI. Please note that we currently extract only a predefined list of 50 biomarkers, so don’t worry if some of your lab results don’t
                appear in the list below.
              </Text>
              <Text fontSize={'14px'} textAlign="left" mb={3}>
                Regardless of which values are extracted, your full lab report document will be uploaded to your chart in the next step.
              </Text>
              <Title textAlign="left">Please review and confirm the information below.</Title>
            </Flex>
            {!!parsedUnconfidentData &&
              Object.keys(parsedUnconfidentData).length > 0 && (
                <>
                  <Flex style={{ backgroundColor: '#FDF0EB', border: '1px solid #FBB9AA', borderRadius: '8px', width: '100%', padding: '16px 20px', gap: '16px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM12 15C11.7348 15 11.4804 15.1054 11.2929 15.2929C11.1054 15.4804 11 15.7348 11 16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17C12.2652 17 12.5196 16.8946 12.7071 16.7071C12.8946 16.5196 13 16.2652 13 16C13 15.7348 12.8946 15.4804 12.7071 15.2929C12.5196 15.1054 12.2652 15 12 15ZM12 6C11.7551 6.00003 11.5187 6.08996 11.3356 6.25272C11.1526 6.41547 11.0357 6.63975 11.007 6.883L11 7V13C11.0003 13.2549 11.0979 13.5 11.2728 13.6854C11.4478 13.8707 11.687 13.9822 11.9414 13.9972C12.1958 14.0121 12.4464 13.9293 12.6418 13.7657C12.8373 13.6021 12.9629 13.3701 12.993 13.117L13 13V7C13 6.73478 12.8946 6.48043 12.7071 6.29289C12.5196 6.10536 12.2652 6 12 6Z"
                        fill="#F48268"
                      />
                    </svg>
                    <Flex flexDirection={'column'} flex={1} style={{ gap: '4px' }}>
                      <Text textAlign={'left'} style={{ color: '#C1160A', fontWeight: 600 }}>
                        {Object.keys(parsedUnconfidentData).length} result
                        {Object.keys(parsedUnconfidentData).length > 1 ? 's' : ''} need your attention
                      </Text>
                      <Text textAlign={'left'} style={{ color: '#363D3F', fontWeight: 400, fontSize: '13px' }}>
                        We're less certain about {Object.keys(parsedUnconfidentData).length > 1 ? 'these' : 'this'} {Object.keys(parsedUnconfidentData).length > 1 ? 'results' : 'result'},<br />
                        Compare it with your lab report.
                      </Text>
                    </Flex>
                  </Flex>
                  {Object.keys(parsedUnconfidentData).map((key) => (
                    <Flex key={key} flexDirection={'column'} style={{ padding: '16px 20px', border: '1px solid #FBB9AA', backgroundColor: 'white', borderRadius: '8px', width: '100%' }}>
                      <label htmlFor={key} style={{ textAlign: 'left', fontWeight: 500 }}>
                        {parsedData[key].labDisplayedName || convertShortAnalyteNameToLong(key)}
                      </label>
                      <Flex mt={2} alignItems={'center'} style={{ gap: '12px' }}>
                        <Flex flexDirection={'column'} flex={1}>
                          <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                            Result
                          </Text>
                          <Input
                            type="text"
                            id={key}
                            name={key}
                            value={parsedData[key].value}
                            onChange={handleChangeUnconfidentAnalyteValue}
                            placeholder="value (e.g. 3.2)"
                            style={{
                              padding: '8px 12px',
                              // flex: 'initial',
                              // width: 'fit-content',
                            }}
                          />
                        </Flex>
                        <Flex flexDirection={'column'} flex={1}>
                          <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                            Unit
                          </Text>

                          <Input
                            type="text"
                            id={key}
                            name={key}
                            value={parsedData[key].units}
                            placeholder="unit (e.g. pg/mL)"
                            onChange={handleChangeUnconfidentAnalyteUnits}
                            style={{
                              padding: '8px 12px',
                              // flex: 'initial',
                              // width: 'fit-content',
                            }}
                          />
                        </Flex>
                      </Flex>
                      <Flex style={{ gap: '12px' }} mt={3}>
                        <CheckboxWrap
                          id={key}
                          name={key}
                          style={{
                            borderColor: '#263B57',
                            cursor: 'pointer',
                            backgroundColor: parsedData[key].isVerified ? '#263B57' : 'transparent',
                            alignSelf: 'baseline',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                          onClick={() => handleChangeUnconfidentAnalyteCheck(key)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M9.00015 16.17L5.53015 12.7C5.34317 12.513 5.08957 12.408 4.82515 12.408C4.56072 12.408 4.30712 12.513 4.12015 12.7C3.93317 12.887 3.82813 13.1406 3.82812 13.405C3.82813 13.5359 3.85391 13.6656 3.90402 13.7866C3.95412 13.9075 4.02756 14.0174 4.12015 14.11L8.30015 18.29C8.69015 18.68 9.32015 18.68 9.71015 18.29L20.2901 7.71002C20.4771 7.52304 20.5822 7.26944 20.5822 7.00502C20.5822 6.74059 20.4771 6.48699 20.2901 6.30002C20.1032 6.11304 19.8496 6.008 19.5851 6.008C19.3207 6.008 19.0671 6.11304 18.8801 6.30002L9.00015 16.17Z"
                              fill="white"
                            />
                          </svg>
                        </CheckboxWrap>
                        <Text flex={1} color="#263B57" fontSize={'13px'} textAlign={'left'} onClick={() => handleChangeUnconfidentAnalyteCheck(key)}>
                          I checked this against my lab report
                        </Text>
                      </Flex>
                    </Flex>
                  ))}
                  {!!parsedConfidentData &&
                    Object.keys(parsedConfidentData).length > 0 && (
                      <Title fontSize={'18px'} fontWeight={500} textAlign="left">
                        Other Results
                      </Title>
                    )}
                </>
              )}

            {parsedConfidentData &&
              Object.keys(parsedConfidentData)
                .filter((key) => key !== 'error' && key !== '__typename')
                .sort((a, b) => parsedData[b].confidence_score - parsedData[a].confidence_score)

                .map((key) => (
                  <Flex
                    key={key}
                    flexDirection={'row'}
                    style={{ padding: '16px 20px', border: '1px solid #EFEEEE', alignItems: 'center', background: 'white', borderRadius: '8px', width: '100%', gap: '12px' }}
                  >
                    {!parsedData[key].isEditing ? (
                      <>
                        <label htmlFor={key} style={{ textAlign: 'left', fontWeight: 500, fontSize: '14px', flex: 1 }}>
                          {parsedData[key].labDisplayedName || convertShortAnalyteNameToLong(key)}
                        </label>
                        <Text textAlign={'right'} fontWeight={400} fontSize="14px">
                          {parsedData[key].value} {parsedData[key].units}
                        </Text>

                        <Button
                          onClick={() => handleChangeConfidentAnalyteEditStatus(key)}
                          variant={'outline'}
                          style={{
                            maxWidth: 'fit-content',
                            maxWidth: 'fit-content',
                            padding: '4px',
                            maxHeight: 'fit-content',
                            borderRadius: '5px',
                            borderWidth: '1px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                            border: '2px solid #EBEBEB',
                            borderColor: '#EBEBEB !important',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M22 7.24002C22.0008 7.10841 21.9756 6.97795 21.9258 6.85611C21.876 6.73427 21.8027 6.62346 21.71 6.53002L17.47 2.29002C17.3766 2.19734 17.2658 2.12401 17.1439 2.07425C17.0221 2.02448 16.8916 1.99926 16.76 2.00002C16.6284 1.99926 16.4979 2.02448 16.3761 2.07425C16.2543 2.12401 16.1435 2.19734 16.05 2.29002L13.22 5.12002L2.29002 16.05C2.19734 16.1435 2.12401 16.2543 2.07425 16.3761C2.02448 16.4979 1.99926 16.6284 2.00002 16.76V21C2.00002 21.2652 2.10537 21.5196 2.29291 21.7071C2.48045 21.8947 2.7348 22 3.00002 22H7.24002C7.37994 22.0076 7.51991 21.9857 7.65084 21.9358C7.78176 21.8858 7.90073 21.8089 8.00002 21.71L18.87 10.78L21.71 8.00002C21.8011 7.90298 21.8755 7.79146 21.93 7.67002C21.9397 7.59031 21.9397 7.50973 21.93 7.43002C21.9347 7.38347 21.9347 7.33657 21.93 7.29002L22 7.24002ZM6.83002 20H4.00002V17.17L13.93 7.24002L16.76 10.07L6.83002 20ZM18.17 8.66002L15.34 5.83002L16.76 4.42002L19.58 7.24002L18.17 8.66002Z"
                              fill="#021444"
                            />
                          </svg>
                        </Button>
                      </>
                    ) : (
                      <Flex flexDirection={'column'} style={{ gap: '12px' }}>
                        <label htmlFor={key} style={{ textAlign: 'left', fontWeight: 500, fontSize: '14px', flex: 1 }}>
                          {parsedData[key].labDisplayedName || convertShortAnalyteNameToLong(key)}
                        </label>

                        <Flex mt={2} alignItems={'center'} style={{ gap: '12px' }}>
                          <Flex flexDirection={'column'} flex={1}>
                            <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                              Result
                            </Text>
                            <Input
                              type="text"
                              id={key}
                              name={key}
                              value={parsedData[key].value}
                              onChange={handleChangeAnalyteValue}
                              placeholder="value (e.g. 3.2)"
                              style={{
                                padding: '8px 12px',
                                flex: 'initial',
                                width: 'fit-content',
                              }}
                            />
                          </Flex>

                          <Flex flexDirection={'column'} flex={1}>
                            <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                              Unit
                            </Text>
                            <Input
                              type="text"
                              id={key}
                              name={key}
                              value={parsedData[key].units}
                              placeholder="unit (e.g. pg/mL)"
                              onChange={handleChangeAnalyteUnits}
                              style={{
                                padding: '8px 12px',
                                flex: 'initial',
                                width: 'fit-content',
                              }}
                            />
                          </Flex>
                        </Flex>

                        <Flex mt={3}>
                          <Flex flex={1} alignItems={'center'} justifyContent={'center'}>
                            <Button
                              variant={'primary'}
                              style={{ maxWidth: 'fit-content', maxHeight: 'fit-content', padding: '6px 18px', fontSize: '14px' }}
                              onClick={() => handleChangeConfidentAnalyteEditStatus(key)}
                            >
                              Done
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                ))}
          </Flex>
        )}
      <Box mt={4} mb={2}>
        <Title textAlign="left">Date of collection</Title>
      </Box>
      <Text textAlign={'left'}>Enter the date your sample was collected</Text>
      <MaskInput2
        inputStyle={{
          style: {
            flex: 'initial',
          },
        }}
        name="dob"
        placeholder="Collection Date (MM/DD/YYYY)"
        mask="99/99/9999"
        value={collectionDate}
        onChange={(e) => {
          // console.log(e.target.value);
          setCollectionDate(e.target.value);
        }}
        id="input_dob"
      />
      {!!errorDate && (
        <Text fontSize={'12px'} color="red">
          {errorDate}
        </Text>
      )}

      {parsedUnconfidentData && Object.keys(parsedUnconfidentData).length > 0 && Object.values(parsedUnconfidentData).filter((item) => !item.isVerified).length > 0 ? (
        <>
          <Flex style={{ borderTop: '1px solid #EBDFD9', paddingTop: '16px', gap: '16px', marginBottom: '16px', alignItems: 'center' }} mt={4}>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M25.1355 13.9545C25.3555 13.7528 25.4864 13.472 25.4993 13.1738C25.5122 12.8756 25.4062 12.5845 25.2045 12.3645C25.0028 12.1445 24.722 12.0136 24.4238 12.0007C24.1256 11.9878 23.8345 12.0938 23.6145 12.2955L15.4095 19.818L12.42 16.83C12.2067 16.6313 11.9247 16.5231 11.6332 16.5282C11.3418 16.5334 11.0637 16.6514 10.8576 16.8576C10.6514 17.0637 10.5334 17.3418 10.5282 17.6332C10.5231 17.9247 10.6313 18.2067 10.83 18.42L14.58 22.17C14.7849 22.375 15.061 22.4931 15.3508 22.4995C15.6406 22.5059 15.9217 22.4003 16.1355 22.2045L25.1355 13.9545ZM30.375 7.5C26.3805 7.5 22.488 6.0855 18.675 3.225C18.4803 3.07895 18.2434 3 18 3C17.7566 3 17.5197 3.07895 17.325 3.225C13.512 6.0855 9.6195 7.5 5.625 7.5C5.32663 7.5 5.04048 7.61853 4.8295 7.8295C4.61853 8.04048 4.5 8.32663 4.5 8.625V16.5C4.5 24.0015 8.937 29.514 17.5875 32.922C17.8526 33.0265 18.1474 33.0265 18.4125 32.922C27.063 29.514 31.5 24 31.5 16.5V8.625C31.5 8.32663 31.3815 8.04048 31.1705 7.8295C30.9595 7.61853 30.6734 7.5 30.375 7.5ZM6.75 9.717C10.6155 9.489 14.37 8.082 18 5.517C21.63 8.082 25.3845 9.489 29.25 9.717V16.5C29.25 22.884 25.5705 27.5685 18 30.663C10.4295 27.5685 6.75 22.884 6.75 16.5V9.717Z"
                fill="#041844"
              />
            </svg>

            <Text fontWeight={500} fontSize={'14px'}>
              Check the flagged result
              {Object.keys(parsedUnconfidentData).length > 1 ? 's' : ''} to continue
            </Text>
          </Flex>
          <Button
            disabled={true}
            onClick={() => {}}
            variant="primary"
            mb={5}
            style={{
              marginLeft: '12px',
              marginRight: '12px',
            }}
          >
            Confirm
          </Button>
        </>
      ) : (
        <Button
          disabled={!collectionDate || !!errorDate}
          onClick={() => onConfirm(collectionDate, parsedData)}
          variant="primary"
          mt={4}
          mb={5}
          style={{
            marginLeft: '12px',
            marginRight: '12px',
          }}
        >
          Confirm
        </Button>
      )}
      {!!isParsing && (
        <>
          <ConfirmModal
            isOpen={true}
            childrenManaged
            onClose={() => {}}
            onBackgroundClick={() => {}}
            onEscapeKeydown={() => {}}
            onConfirm={async () => {
              // wizard.next();
            }}
            confirmLabel="Save"
            cancelLabel="Remove"
            confirmStyle={{
              width: '100%',
            }}
            cancelStyle={{
              width: '100%',
              marginTop: '18px',
            }}
            cancelVariant="outline"
            buttonsDirection="column"
            displayX={false}
            cardStyle={{
              maxWidth: 'calc(100vw - 40px)',
            }}
          >
            <ModalInner>
              <Flex flexDirection={'column'} p={3} alignItems={'center'} justifyContent={'center'}>
                <SpinLoader />
                <Text mt={3}>Analyzing Lab Order</Text>
                <Text mb={3}>This may take a few seconds depending on the file size</Text>
              </Flex>
            </ModalInner>
          </ConfirmModal>
        </>
      )}
    </Flex>
  );
};

const PdfMiniature = ({ file, onFileRemove, onConfirm, client }) => {
  const [collectionDate, setCollectionDate] = useState();
  const [errorDate, setErrorDate] = useState();
  const [parsedData, setParsedData] = useState();
  const [parsedUnconfidentData, setParsedUnconfidentData] = useState();
  const [parsedConfidentData, setParsedConfidentData] = useState();
  const [isParsing, setIsParsing] = useState(true);

  useEffect(
    () => {
      checkDate();
    },
    [collectionDate],
  );

  useEffect(() => {
    setIsParsing(true);
    // console.log('HH', file);
    if (file && client) {
      const getData = async () => {
        const { data } = await client.query({
          query: qryParseDocumentAi,
          fetchPolicy: 'network-only',
          variables: {
            document: file.data,
          },
        });

        if (data && data.parseLabDocumentValuesExtended && data.parseLabDocumentValuesExtended.biomarkers) {
          const parsed = JSON.parse(data.parseLabDocumentValuesExtended.biomarkers);
          const confident = Object.fromEntries(Object.entries(parsed).filter(([key, value]) => key !== 'error' && key !== '__typename' && (!!value.confidence_score && value.confidence_score >= 90)));

          const unconfident = Object.fromEntries(Object.entries(parsed).filter(([key, value]) => key !== 'error' && key !== '__typename' && (!value.confidence_score || value.confidence_score < 90)));

          setParsedData(parsed);
          setParsedConfidentData(confident);
          setParsedUnconfidentData(unconfident);
          // console.log({ parsed });
          // if (parsed.fT3) {
          // setCollectionDate((parsed.fT3 || {}).collectionDate);
          let collectionD = (parsed.fT3 || {}).collectionDate;
          // console.log({ collectionD });

          if (!collectionD) {
            const firstCollection = Object.values(parsed).find((item) => item.collectionDate !== undefined);
            // console.log({ firstCollection });
            if (firstCollection) {
              collectionD = firstCollection.collectionDate;
            }
          }
          // console.log({ collectionD2: collectionD });
          setCollectionDate(collectionD);
          // }
        }
        setIsParsing(false);
      };
      getData();
    }
  }, []);

  // Handle input change
  const handleChangeAnalyteValue = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      value: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  // Handle input change
  const handleChangeAnalyteUnits = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      units: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  // Handle input change
  const handleChangeUnconfidentAnalyteValue = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      value: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedUnconfidentData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };
  const handleChangeConfidentAnalyteEditStatus = (name) => {
    const prev = parsedData[name].isEditing;
    let newVal = {
      ...parsedData[name],
      isEditing: !prev,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };

  // Handle input change
  const handleChangeUnconfidentAnalyteUnits = (e) => {
    const { name, value } = e.target;
    let newVal = {
      ...parsedData[name],
      units: value,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedUnconfidentData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };
  // Handle input change
  const handleChangeUnconfidentAnalyteCheck = (name) => {
    const prev = parsedData[name].isVerified;
    let newVal = {
      ...parsedData[name],
      isVerified: !prev,
    };
    setParsedData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
    setParsedUnconfidentData((prevState) => ({
      ...prevState,
      [name]: {
        ...newVal,
      },
    }));
  };
  const checkDate = () => {
    if (!collectionDate || collectionDate.indexOf('_') > -1) {
      setErrorDate('Required');
      return false;
    }
    try {
      const d = moment(collectionDate, 'MM/DD/YYYY');
      if (d.isAfter(moment(), 'day')) {
        setErrorDate('Date must be in the past');
        return false;
      }
      setErrorDate(null);
      return true;
    } catch (error) {
      setErrorDate('Invalid date');
      return false;
    }
  };
  // console.log({ collectionDate });
  return (
    <Flex flex={1} flexDirection="column" style={{ zIndex: 1, maxWidth: 'max(50%, 350px)', alignSelf: 'center', width: '100%' }}>
      {!!parsedData &&
        Object.keys(parsedData).filter((key) => key !== 'error' && key !== '__typename').length > 0 && (
          <Flex style={{ gap: '16px' }} flexDirection={'column'}>
            <Box mt="0" mb={4}>
              <Heading textAlign="center">Please review your lab values</Heading>
            </Box>

            <Flex flexDirection={'column'} mb={2}>
              <Text fontSize={'14px'} textAlign="left" mb={2}>
                We’ve automatically extracted your lab values using AI. Please note that we currently extract only a predefined list of 50 biomarkers, so don’t worry if some of your lab results don’t
                appear in the list below.
              </Text>
              <Text fontSize={'14px'} textAlign="left" mb={3}>
                Regardless of which values are extracted, your full lab report document will be uploaded to your chart in the next step.
              </Text>
              <Title textAlign="left">Please review and confirm the information below.</Title>
            </Flex>

            {!!parsedUnconfidentData &&
              Object.keys(parsedUnconfidentData).length > 0 && (
                <>
                  <Flex style={{ backgroundColor: '#FDF0EB', border: '1px solid #FBB9AA', borderRadius: '8px', width: '100%', padding: '16px 20px', gap: '16px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM12 15C11.7348 15 11.4804 15.1054 11.2929 15.2929C11.1054 15.4804 11 15.7348 11 16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17C12.2652 17 12.5196 16.8946 12.7071 16.7071C12.8946 16.5196 13 16.2652 13 16C13 15.7348 12.8946 15.4804 12.7071 15.2929C12.5196 15.1054 12.2652 15 12 15ZM12 6C11.7551 6.00003 11.5187 6.08996 11.3356 6.25272C11.1526 6.41547 11.0357 6.63975 11.007 6.883L11 7V13C11.0003 13.2549 11.0979 13.5 11.2728 13.6854C11.4478 13.8707 11.687 13.9822 11.9414 13.9972C12.1958 14.0121 12.4464 13.9293 12.6418 13.7657C12.8373 13.6021 12.9629 13.3701 12.993 13.117L13 13V7C13 6.73478 12.8946 6.48043 12.7071 6.29289C12.5196 6.10536 12.2652 6 12 6Z"
                        fill="#F48268"
                      />
                    </svg>
                    <Flex flexDirection={'column'} flex={1} style={{ gap: '4px' }}>
                      <Text textAlign={'left'} style={{ color: '#C1160A', fontWeight: 600 }}>
                        {Object.keys(parsedUnconfidentData).length} result
                        {Object.keys(parsedUnconfidentData).length > 1 ? 's' : ''} need your attention
                      </Text>
                      <Text textAlign={'left'} style={{ color: '#363D3F', fontWeight: 400, fontSize: '13px' }}>
                        We're less certain about {Object.keys(parsedUnconfidentData).length > 1 ? 'these' : 'this'} {Object.keys(parsedUnconfidentData).length > 1 ? 'results' : 'result'},<br />
                        Compare it with your lab report.
                      </Text>
                    </Flex>
                  </Flex>
                  {Object.keys(parsedUnconfidentData).map((key) => (
                    <Flex key={key} flexDirection={'column'} style={{ padding: '16px 20px', border: '1px solid #FBB9AA', backgroundColor: 'white', borderRadius: '8px', width: '100%' }}>
                      <label htmlFor={key} style={{ textAlign: 'left', fontWeight: 500 }}>
                        {parsedData[key].labDisplayedName || convertShortAnalyteNameToLong(key)}
                      </label>
                      <Flex mt={2} alignItems={'center'} style={{ gap: '12px' }}>
                        <Flex flexDirection={'column'} flex={1}>
                          <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                            Result
                          </Text>
                          <Input
                            type="text"
                            id={key}
                            name={key}
                            value={parsedData[key].value}
                            onChange={handleChangeUnconfidentAnalyteValue}
                            placeholder="value (e.g. 3.2)"
                            style={{
                              padding: '8px 12px',
                              // flex: 'initial',
                              // width: 'fit-content',
                            }}
                          />
                        </Flex>
                        <Flex flexDirection={'column'} flex={1}>
                          <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                            Unit
                          </Text>

                          <Input
                            type="text"
                            id={key}
                            name={key}
                            value={parsedData[key].units}
                            placeholder="unit (e.g. pg/mL)"
                            onChange={handleChangeUnconfidentAnalyteUnits}
                            style={{
                              padding: '8px 12px',
                              // flex: 'initial',
                              // width: 'fit-content',
                            }}
                          />
                        </Flex>
                      </Flex>
                      <Flex style={{ gap: '12px' }} mt={3}>
                        <CheckboxWrap
                          id={key}
                          name={key}
                          style={{
                            borderColor: '#263B57',
                            cursor: 'pointer',
                            backgroundColor: parsedData[key].isVerified ? '#263B57' : 'transparent',
                            alignSelf: 'baseline',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                          onClick={() => handleChangeUnconfidentAnalyteCheck(key)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M9.00015 16.17L5.53015 12.7C5.34317 12.513 5.08957 12.408 4.82515 12.408C4.56072 12.408 4.30712 12.513 4.12015 12.7C3.93317 12.887 3.82813 13.1406 3.82812 13.405C3.82813 13.5359 3.85391 13.6656 3.90402 13.7866C3.95412 13.9075 4.02756 14.0174 4.12015 14.11L8.30015 18.29C8.69015 18.68 9.32015 18.68 9.71015 18.29L20.2901 7.71002C20.4771 7.52304 20.5822 7.26944 20.5822 7.00502C20.5822 6.74059 20.4771 6.48699 20.2901 6.30002C20.1032 6.11304 19.8496 6.008 19.5851 6.008C19.3207 6.008 19.0671 6.11304 18.8801 6.30002L9.00015 16.17Z"
                              fill="white"
                            />
                          </svg>
                        </CheckboxWrap>
                        <Text flex={1} color="#263B57" fontSize={'13px'} textAlign={'left'} onClick={() => handleChangeUnconfidentAnalyteCheck(key)}>
                          I checked this against my lab report
                        </Text>
                      </Flex>
                    </Flex>
                  ))}
                  {!!parsedConfidentData &&
                    Object.keys(parsedConfidentData).length > 0 && (
                      <Title fontSize={'18px'} fontWeight={500} textAlign="left">
                        Other Results
                      </Title>
                    )}
                </>
              )}

            {parsedConfidentData &&
              Object.keys(parsedConfidentData)
                .filter((key) => key !== 'error' && key !== '__typename')
                .sort((a, b) => parsedData[b].confidence_score - parsedData[a].confidence_score)

                .map((key) => (
                  <Flex
                    key={key}
                    flexDirection={'row'}
                    style={{ padding: '16px 20px', border: '1px solid #EFEEEE', alignItems: 'center', background: 'white', borderRadius: '8px', width: '100%', gap: '12px' }}
                  >
                    {!parsedData[key].isEditing ? (
                      <>
                        <label htmlFor={key} style={{ textAlign: 'left', fontWeight: 500, fontSize: '14px', flex: 1 }}>
                          {parsedData[key].labDisplayedName || convertShortAnalyteNameToLong(key)}
                        </label>
                        <Text textAlign={'right'} fontWeight={400} fontSize="14px">
                          {parsedData[key].value} {parsedData[key].units}
                        </Text>

                        <Button
                          onClick={() => handleChangeConfidentAnalyteEditStatus(key)}
                          variant={'outline'}
                          style={{
                            maxWidth: 'fit-content',
                            maxWidth: 'fit-content',
                            padding: '4px',
                            maxHeight: 'fit-content',
                            borderRadius: '5px',
                            borderWidth: '1px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M22 7.24002C22.0008 7.10841 21.9756 6.97795 21.9258 6.85611C21.876 6.73427 21.8027 6.62346 21.71 6.53002L17.47 2.29002C17.3766 2.19734 17.2658 2.12401 17.1439 2.07425C17.0221 2.02448 16.8916 1.99926 16.76 2.00002C16.6284 1.99926 16.4979 2.02448 16.3761 2.07425C16.2543 2.12401 16.1435 2.19734 16.05 2.29002L13.22 5.12002L2.29002 16.05C2.19734 16.1435 2.12401 16.2543 2.07425 16.3761C2.02448 16.4979 1.99926 16.6284 2.00002 16.76V21C2.00002 21.2652 2.10537 21.5196 2.29291 21.7071C2.48045 21.8947 2.7348 22 3.00002 22H7.24002C7.37994 22.0076 7.51991 21.9857 7.65084 21.9358C7.78176 21.8858 7.90073 21.8089 8.00002 21.71L18.87 10.78L21.71 8.00002C21.8011 7.90298 21.8755 7.79146 21.93 7.67002C21.9397 7.59031 21.9397 7.50973 21.93 7.43002C21.9347 7.38347 21.9347 7.33657 21.93 7.29002L22 7.24002ZM6.83002 20H4.00002V17.17L13.93 7.24002L16.76 10.07L6.83002 20ZM18.17 8.66002L15.34 5.83002L16.76 4.42002L19.58 7.24002L18.17 8.66002Z"
                              fill="#021444"
                            />
                          </svg>
                        </Button>
                      </>
                    ) : (
                      <Flex flexDirection={'column'} style={{ gap: '12px' }}>
                        <label htmlFor={key} style={{ textAlign: 'left', fontWeight: 500, fontSize: '14px', flex: 1 }}>
                          {parsedData[key].labDisplayedName || convertShortAnalyteNameToLong(key)}
                        </label>

                        <Flex mt={2} alignItems={'center'} style={{ gap: '12px' }}>
                          <Flex flexDirection={'column'} flex={1}>
                            <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                              Result
                            </Text>
                            <Input
                              type="text"
                              id={key}
                              name={key}
                              value={parsedData[key].value}
                              onChange={handleChangeAnalyteValue}
                              placeholder="value (e.g. 3.2)"
                              style={{
                                padding: '8px 12px',
                                flex: 'initial',
                                width: 'fit-content',
                              }}
                            />
                          </Flex>

                          <Flex flexDirection={'column'} flex={1}>
                            <Text fontSize={'13px'} color={'#363D3F'} textAlign={'left'}>
                              Unit
                            </Text>
                            <Input
                              type="text"
                              id={key}
                              name={key}
                              value={parsedData[key].units}
                              placeholder="unit (e.g. pg/mL)"
                              onChange={handleChangeAnalyteUnits}
                              style={{
                                padding: '8px 12px',
                                flex: 'initial',
                                width: 'fit-content',
                              }}
                            />
                          </Flex>
                        </Flex>

                        <Flex mt={3}>
                          <Flex flex={1} alignItems={'center'} justifyContent={'center'}>
                            <Button
                              variant={'primary'}
                              style={{ maxWidth: 'fit-content', maxHeight: 'fit-content', padding: '6px 18px', fontSize: '14px' }}
                              onClick={() => handleChangeConfidentAnalyteEditStatus(key)}
                            >
                              Done
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                ))}
          </Flex>
        )}
      <Box mt="4" mb={2}>
        <Heading textAlign="center">Date of collection</Heading>
      </Box>
      <Text>Enter the date your sample was collected</Text>
      <MaskInput2
        inputProps={{
          style: {
            flex: 'initial',
          },
        }}
        name="dob"
        placeholder="Collection Date (MM/DD/YYYY)"
        mask="99/99/9999"
        value={collectionDate}
        onChange={(e) => {
          // console.log(e.target.value);
          setCollectionDate(e.target.value);
        }}
        id="input_dob"
      />

      {!!errorDate && (
        <Text fontSize={'12px'} color="red">
          {errorDate}
        </Text>
      )}

      {parsedUnconfidentData && Object.values(parsedUnconfidentData).filter((item) => !item.isVerified).length > 0 ? (
        <>
          <Flex style={{ borderTop: '1px solid #EBDFD9', paddingTop: '16px', gap: '16px', marginBottom: '16px', alignItems: 'center' }} mt={4}>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M25.1355 13.9545C25.3555 13.7528 25.4864 13.472 25.4993 13.1738C25.5122 12.8756 25.4062 12.5845 25.2045 12.3645C25.0028 12.1445 24.722 12.0136 24.4238 12.0007C24.1256 11.9878 23.8345 12.0938 23.6145 12.2955L15.4095 19.818L12.42 16.83C12.2067 16.6313 11.9247 16.5231 11.6332 16.5282C11.3418 16.5334 11.0637 16.6514 10.8576 16.8576C10.6514 17.0637 10.5334 17.3418 10.5282 17.6332C10.5231 17.9247 10.6313 18.2067 10.83 18.42L14.58 22.17C14.7849 22.375 15.061 22.4931 15.3508 22.4995C15.6406 22.5059 15.9217 22.4003 16.1355 22.2045L25.1355 13.9545ZM30.375 7.5C26.3805 7.5 22.488 6.0855 18.675 3.225C18.4803 3.07895 18.2434 3 18 3C17.7566 3 17.5197 3.07895 17.325 3.225C13.512 6.0855 9.6195 7.5 5.625 7.5C5.32663 7.5 5.04048 7.61853 4.8295 7.8295C4.61853 8.04048 4.5 8.32663 4.5 8.625V16.5C4.5 24.0015 8.937 29.514 17.5875 32.922C17.8526 33.0265 18.1474 33.0265 18.4125 32.922C27.063 29.514 31.5 24 31.5 16.5V8.625C31.5 8.32663 31.3815 8.04048 31.1705 7.8295C30.9595 7.61853 30.6734 7.5 30.375 7.5ZM6.75 9.717C10.6155 9.489 14.37 8.082 18 5.517C21.63 8.082 25.3845 9.489 29.25 9.717V16.5C29.25 22.884 25.5705 27.5685 18 30.663C10.4295 27.5685 6.75 22.884 6.75 16.5V9.717Z"
                fill="#041844"
              />
            </svg>

            <Text fontWeight={500} fontSize={'14px'}>
              Check the flagged result
              {Object.keys(parsedUnconfidentData).length > 1 ? 's' : ''} to continue
            </Text>
          </Flex>
          <Button
            disabled={true}
            onClick={() => {}}
            variant="primary"
            mb={5}
            style={{
              marginLeft: '12px',
              marginRight: '12px',
            }}
          >
            Confirm
          </Button>
        </>
      ) : (
        <Button
          disabled={!collectionDate || !!errorDate}
          onClick={() => onConfirm(collectionDate, parsedData)}
          variant="primary"
          mt={4}
          mb={5}
          style={{
            marginLeft: '12px',
            marginRight: '12px',
          }}
        >
          Confirm
        </Button>
      )}
      {!!isParsing && (
        <>
          <ConfirmModal
            isOpen={true}
            childrenManaged
            onClose={() => {}}
            onBackgroundClick={() => {}}
            onEscapeKeydown={() => {}}
            onConfirm={async () => {
              // wizard.next();
            }}
            confirmLabel="Save"
            cancelLabel="Remove"
            confirmStyle={{
              width: '100%',
            }}
            cancelStyle={{
              width: '100%',
              marginTop: '18px',
            }}
            cancelVariant="outline"
            buttonsDirection="column"
            displayX={false}
            cardStyle={{
              maxWidth: 'calc(100vw - 40px)',
            }}
          >
            <ModalInner>
              <Flex flexDirection={'column'} p={3} alignItems={'center'} justifyContent={'center'}>
                <SpinLoader />
                <Text mt={3}>Analyzing Lab Order</Text>
                <Text mb={3}>This may take a few seconds depending on the file size</Text>
              </Flex>
            </ModalInner>
          </ConfirmModal>
        </>
      )}
    </Flex>
  );
};

const UPLOAD_LAB_RESULTS = gql`
  mutation($file: String!, $collectionDate: String, $analytes: String) {
    uploadLabResultAsync(file: $file, collectionDate: $collectionDate, analytes: $analytes) {
      ok
    }
  }
`;

const EmptyState = ({ onDropAccepted, takeDesktopPhoto = () => {}, toastManager, disabled }) => {
  return (
    <Dropzone
      disableClick={disabled}
      disabled={disabled}
      noClick={false}
      multiple={false}
      onDropRejected={() => {
        toastManager.add('Make sure your file is a pdf or a jpeg or a png and less than 15 MB', {
          appearance: 'error',
          autoDismissTimeout: 10000,
        });
      }}
      maxSize={15000000}
      onDropAccepted={onDropAccepted}
      accept={'image/jpeg, image/png, application/pdf'}
      style={{
        marginBottom: 36,
      }}
    >
      {({ getRootProps, getInputProps, isDragActive, open }) => (
        <EmptyStateFlexWrapper
          flexDirection="column"
          {...getRootProps()}
          onClick={() => {
            open();
          }}
        >
          {isMobile ? (
            <Button variant="pink" disabled={disabled}>
              + Upload Lab
            </Button>
          ) : (
            <Flex flexDirection="row" justifyContent="center">
              <Button variant="pink" mr={2} disabled={disabled}>
                + Select a File
              </Button>
              {/* <Button
                variant="pink"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  takeDesktopPhoto();
                }}
                ml={2}
                disabled={disabled}
              >
                + Take Photo
              </Button> */}
            </Flex>
          )}

          <input type="file" {...getInputProps()} />
        </EmptyStateFlexWrapper>
      )}
    </Dropzone>
  );
};

const onDesktopPhotoCapture = async (dta, callback) => {
  var reader = new FileReader();
  reader.readAsDataURL(dta);
  reader.onloadend = async () => {
    var base64data = reader.result;

    const fls = {
      fileName: `${uuidv4()}.jpg`,
      mimeType: 'image/jpeg',
      data: base64data,
    };

    callback(fls);
  };
};

const fileAccepted = (f, callback) => {
  for (let index = 0; index < f.length; index++) {
    const element = f[index];

    const reader = new FileReader();
    reader.onload = (event) => {
      let fls = f;
      fls = {
        fileName: element.name,
        mimeType: element.type,
        data: event.target.result,
      };

      callback(fls);
    };
    reader.readAsDataURL(element);
  }
};

const UploadLabResultsExtended = ({ onSubmit, data, gotoStep, client, toastManager, wizard, isStandaloneMode, history, isMeno, isNewIntakeIntegration, onSkip }) => {
  const [labs, setLabs] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isCardFrontTaking, setIsCardFrontTaking] = useState(false);
  const [isCardFrontConfirming, setIsCardFrontConfirming] = useState(false);
  const [isPdfConfirming, setIsPdfConfirming] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);

  if (isCardFrontTaking) {
    return (
      <Flex flex={1} flexDirection="column" style={{ zIndex: 1 }}>
        <PhotoTaker
          onCapture={(f) =>
            onDesktopPhotoCapture(f, (ff) => {
              setLabs((oldArr) => [...oldArr, ff]);
              setIsCardFrontConfirming(true);
              setIsCardFrontTaking(false);
            })
          }
        />
      </Flex>
    );
  }

  return (
    <Query query={LABS}>
      {({ loading: loadingLabs, data: dataLabs }) => {
        const getCanBypass = () => {
          if (!!isMeno) return false;
          if (!!isStandaloneMode || !!isNewIntakeIntegration) return true;
          let canBypass = false;
          try {
            if (!!dataLabs && !!dataLabs.labs) {
              if (dataLabs.labs.length > 0) {
                for (let index = 0; index < dataLabs.labs.length; index++) {
                  const element = dataLabs.labs[index];
                  if (!element || !element.status) continue;

                  if (element.status === 'registered' || element.status === 'scannedByLab') {
                    let vars = element.created;
                    if (element.currentStep && element.currentStep.delivered) {
                      vars = element.currentStep.delivered;
                    }
                    let isNotWithinTime = moment(vars).diff(moment(), 'day') < THRESHOLD_LAB_VALIDITY_DAYS;
                    if (!isNotWithinTime) {
                      canBypass = true;
                      break;
                    }
                  } else {
                    if (!!element.resultedAt) {
                      let isNotWithinTime = moment(element.resultedAt).diff(moment(), 'day') < 120;
                      if (!isNotWithinTime) {
                        canBypass = true;
                        break;
                      }
                    }
                  }
                }
              }
            }
          } catch (error) {}

          return canBypass;
        };
        if (!!loadingLabs) return <PleaseWait />;
        return (
          <Fragment>
            {!isCardFrontConfirming && !isPdfConfirming ? (
              <Flex flex={1} flexDirection="column" style={{ zIndex: 1, alignSelf: 'center', alignItems: 'center', padding: '0 12px' }}>
                <Box mt="0" mb={3}>
                  <Title textAlign="center">{data.prompt || 'Please select labs to upload'}</Title>
                </Box>

                <Flex mt="0" mb={4} flexDirection={'column'} alignItems={'center'}>
                  <Description>Prepare for your visit by uploading labs that are:</Description>
                  <ChecksContainer>
                    <Flex>
                      <svg style={{ marginTop: '2px' }} width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="-0.00012207" width="20" height="20" rx="10" fill="#49FF66" />
                        <path d="M4.82751 10L8.70682 15.1725L15.1723 4.82764" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <Description flex={1} ml={3} textAlign={'left'}>
                        Less than 4 months old
                      </Description>
                    </Flex>
                    <Flex mt={3}>
                      <svg style={{ marginTop: '2px' }} width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="-0.00012207" width="20" height="20" rx="10" fill="#49FF66" />
                        <path d="M4.82751 10L8.70682 15.1725L15.1723 4.82764" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <Description flex={1} ml={3} textAlign={'left'}>
                        Ensure the document displays&nbsp;
                        <span style={{ fontStyle: 'italic' }}>both</span>
                        &nbsp;the lab name and your name
                      </Description>
                    </Flex>
                    <Flex mt={3}>
                      <svg style={{ marginTop: '2px' }} width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="-0.00012207" width="20" height="20" rx="10" fill="#49FF66" />
                        <path d="M4.82751 10L8.70682 15.1725L15.1723 4.82764" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <Description flex={1} ml={3} textAlign={'left'}>
                        {`Preferably upload as PDFs; avoid screenshots from your phone`}
                      </Description>
                    </Flex>
                  </ChecksContainer>
                  <Description mt={3} textAlign={'center'}>
                    This ensures your doctor can prescribe medications.
                  </Description>
                  <Description mt={3} textAlign={'center'} style={{ fontWeight: 600 }}>
                    If you are having any trouble uploading your labs, please email them to:&nbsp;
                    <a href="mailto:care@palomahealth.com">care@palomahealth.com</a>.
                  </Description>
                </Flex>

                <Flex flexDirection="column" style={{ maxWidth: '350px', alignSelf: 'center', width: '100%' }}>
                  {labs.map((x, i) => {
                    return (
                      <Flex key={`uploadLab${i}-${x.name}`} mb={3}>
                        <LabToUpload>
                          <svg width="23" height="26" viewBox="0 0 23 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M6.11702 7.0367V5.6055C6.11702 5.47431 6.22713 5.36697 6.3617 5.36697H18.1064C18.241 5.36697 18.3511 5.47431 18.3511 5.6055V7.0367C18.3511 7.16789 18.241 7.27523 18.1064 7.27523H6.3617C6.22713 7.27523 6.11702 7.16789 6.11702 7.0367ZM18.1064 9.66055C18.241 9.66055 18.3511 9.76789 18.3511 9.89908V11.3303C18.3511 11.4615 18.241 11.5688 18.1064 11.5688H12.4787C12.3441 11.5688 12.234 11.4615 12.234 11.3303V9.89908C12.234 9.76789 12.3441 9.66055 12.4787 9.66055H18.1064ZM6.60638 13.1193C10.2552 13.1193 13.2128 16.0025 13.2128 19.5596C13.2128 23.1167 10.2552 26 6.60638 26C2.95758 26 0 23.1167 0 19.5596C0 16.0025 2.95758 13.1193 6.60638 13.1193ZM3.31848 22.7649C4.19628 23.6206 5.36463 24.0917 6.60638 24.0917C7.84814 24.0917 9.01649 23.6206 9.89428 22.7649C10.7721 21.9092 11.2553 20.7702 11.2553 19.5596C11.2553 18.3491 10.7721 17.2101 9.89428 16.3544C9.01649 15.4986 7.84814 15.0275 6.60638 15.0275C5.36463 15.0275 4.19628 15.4986 3.31848 16.3544C2.44069 17.2101 1.95745 18.3491 1.95745 19.5596C1.95745 20.7702 2.44069 21.9092 3.31848 22.7649ZM3.88431 17.2936H5.23923C5.31875 17.2936 5.39215 17.3294 5.43803 17.392L7.38019 20.0099L8.0867 19.0587C8.10933 19.028 8.1392 19.003 8.1738 18.9859C8.2084 18.9688 8.24671 18.96 8.28551 18.9603H9.63431C9.83311 18.9603 9.94934 19.181 9.83311 19.339L7.57593 22.3833C7.47806 22.5145 7.27925 22.5145 7.18138 22.3833L3.68856 17.6722C3.56928 17.5142 3.6855 17.2936 3.88431 17.2936ZM13.7021 23.1376H20.7979V2.14679H3.67021V12.4037C3.67021 12.5349 3.56011 12.6422 3.42553 12.6422H1.71277C1.57819 12.6422 1.46808 12.5349 1.46808 12.4037V0.954128C1.46808 0.426376 1.90545 0 2.44681 0H22.0213C22.5626 0 23 0.426376 23 0.954128V24.3303C23 24.858 22.5626 25.2844 22.0213 25.2844H13.7021C13.5676 25.2844 13.4574 25.1771 13.4574 25.0459V23.3761C13.4574 23.245 13.5676 23.1376 13.7021 23.1376Z"
                              fill="#354E79"
                              fillOpacity="0.7"
                            />
                          </svg>

                          <Text ml={2} fontSize="14px" fontWeight="300">
                            {x.fileName}
                          </Text>

                          <svg
                            onClick={() => {
                              if (isSending) return;
                              const temp = [...labs];

                              temp.splice(i, 1);
                              setLabs(temp);
                            }}
                            style={{
                              position: 'absolute',
                              right: -11,
                              top: -11,
                              cursor: isSending ? 'not-allowed' : 'pointer',
                            }}
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15.3398 8.32205C15.4358 8.23249 15.5133 8.12488 15.5678 8.00538C15.6223 7.88587 15.6527 7.75681 15.6573 7.62555C15.6619 7.4943 15.6406 7.36342 15.5946 7.2404C15.5486 7.11737 15.4788 7.00461 15.3893 6.90855C15.2997 6.81249 15.1921 6.73501 15.0726 6.68053C14.9531 6.62605 14.824 6.59565 14.6928 6.59105C14.5615 6.58646 14.4306 6.60776 14.3076 6.65374C14.1846 6.69972 14.0718 6.76949 13.9758 6.85905L11.0498 9.58705L8.32176 6.66005C8.13923 6.47309 7.89078 6.36501 7.62956 6.35891C7.36834 6.35282 7.11512 6.44921 6.92407 6.62745C6.73302 6.8057 6.61932 7.05164 6.6073 7.31265C6.59529 7.57366 6.6859 7.829 6.85977 8.02405L9.58777 10.95L6.66077 13.6781C6.56131 13.7667 6.48057 13.8743 6.4233 13.9946C6.36602 14.1149 6.33336 14.2454 6.32723 14.3785C6.3211 14.5116 6.34163 14.6446 6.38761 14.7696C6.43359 14.8947 6.5041 15.0093 6.59498 15.1067C6.68587 15.2041 6.79531 15.2824 6.91687 15.3369C7.03843 15.3915 7.16966 15.4212 7.30286 15.4243C7.43605 15.4274 7.56853 15.4038 7.6925 15.355C7.81647 15.3062 7.92944 15.2331 8.02476 15.14L10.9508 12.413L13.6788 15.3391C13.7668 15.4403 13.8744 15.5229 13.9951 15.5817C14.1157 15.6405 14.247 15.6745 14.381 15.6815C14.5151 15.6885 14.6492 15.6685 14.7753 15.6226C14.9015 15.5767 15.0171 15.5058 15.1153 15.4143C15.2134 15.3228 15.2922 15.2124 15.3468 15.0898C15.4014 14.9672 15.4308 14.8348 15.4332 14.7006C15.4356 14.5664 15.4109 14.4331 15.3607 14.3086C15.3104 14.1841 15.2356 14.071 15.1408 13.976L12.4138 11.05L15.3398 8.32205Z"
                              fill="#354E79"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0 11C0 4.925 4.925 0 11 0C17.075 0 22 4.925 22 11C22 17.075 17.075 22 11 22C4.925 22 0 17.075 0 11ZM11 20C9.8181 20 8.64778 19.7672 7.55585 19.3149C6.46392 18.8626 5.47177 18.1997 4.63604 17.364C3.80031 16.5282 3.13738 15.5361 2.68508 14.4442C2.23279 13.3522 2 12.1819 2 11C2 9.8181 2.23279 8.64778 2.68508 7.55585C3.13738 6.46392 3.80031 5.47177 4.63604 4.63604C5.47177 3.80031 6.46392 3.13738 7.55585 2.68508C8.64778 2.23279 9.8181 2 11 2C13.3869 2 15.6761 2.94821 17.364 4.63604C19.0518 6.32387 20 8.61305 20 11C20 13.3869 19.0518 15.6761 17.364 17.364C15.6761 19.0518 13.3869 20 11 20Z"
                              fill="#354E79"
                            />
                          </svg>
                        </LabToUpload>
                      </Flex>
                    );
                  })}
                </Flex>

                <EmptyState
                  disabled={isSending}
                  toastManager={toastManager}
                  onDropAccepted={(f) => {
                    fileAccepted(f, (ff) => {
                      // console.log(ff);
                      setLabs((oldArr) => [...oldArr, ff]);
                      if (
                        ff &&
                        ff.mimeType &&
                        ff.mimeType
                          .toString()
                          .toLowerCase()
                          .startsWith('image/')
                      ) {
                        setIsCardFrontConfirming(true);
                      } else {
                        setIsPdfConfirming(true);
                      }
                    });
                  }}
                  takeDesktopPhoto={() => {
                    setIsCardFrontTaking(true);
                  }}
                />
                {/* <Box mt={4} height="1px" />*/}
                {data.skip ? (
                  <Box
                    // style={{ borderTop: '1px solid rgba(0,0,0,0.3)' }}
                    mt={4}
                    p="12px"
                    mb={4}
                    onClick={() => {
                      if (getCanBypass()) {
                        wizard.next();
                      } else {
                        setConfirmSkip(true);
                      }
                    }}
                  >
                    <FakeLink>
                      Skip
                      <span style={{ color: '#F6A293' }}>{' >'}</span>
                    </FakeLink>
                  </Box>
                ) : null}

                <Box mt={4} mb={3} width="100%" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Button
                    disabled={isSending}
                    variant="primary"
                    style={{
                      marginLeft: '12px',
                      marginRight: '12px',
                    }}
                    width={[1 / 2, 1 / 2, 1 / 4]}
                    onClick={async () => {
                      if (labs.length < 1) {
                        if (getCanBypass()) {
                          if (!!isNewIntakeIntegration && onSkip) {
                            onSkip();
                          } else if (!!isStandaloneMode) {
                            history.push('/uploadLabResultsSuccess');
                          } else {
                            wizard.next();
                          }
                        } else {
                          setConfirmSkip(true);
                        }
                        return;
                      }
                      setIsSending(true);
                      for (let index = 0; index < labs.length; index++) {
                        const element = labs[index];
                        // console.log({ a: element.analytes, p: JSON.stringify(element.analytes) });
                        // setIsSending(false);
                        // return
                        if (isNewIntakeIntegration) {
                          await client.mutate({
                            mutation: UPLOAD_LAB_RESULTS,
                            variables: { file: element.data, collectionDate: element.collectionDate, analytes: element.analytes ? JSON.stringify(element.analytes) : null },
                            refetchQueries: [{ query: GET_LABS_LIST }, { query: LABS }],
                            awaitRefetchQueries: true,
                          });
                        } else {
                          await client.mutate({
                            mutation: UPLOAD_LAB_RESULTS,
                            variables: { file: element.data, collectionDate: element.collectionDate, analytes: element.analytes ? JSON.stringify(element.analytes) : null },
                            refetchQueries: [{ query: GET_LABS_LIST }, { query: LABS }],
                            awaitRefetchQueries: true,
                          });
                        }
                      }
                      setIsSending(false);
                      if (!!isNewIntakeIntegration && onSkip) {
                        onSkip();
                      } else if (!!isStandaloneMode) {
                        history.push('/uploadLabResultsSuccess');
                      } else {
                        // if (data.onNextRoute) {
                        //   gotoStep(data.onNextRoute);
                        // } else {
                        wizard.next();
                        // }
                      }
                    }}
                  >
                    {isSending ? 'Uploading...' : isStandaloneMode ? `Continue` : `Continue`}
                  </Button>
                </Box>
              </Flex>
            ) : isCardFrontConfirming ? (
              <Flex flex={1} flexDirection="column" style={{ zIndex: 1 }}>
                <PictureMiniature
                  client={client}
                  file={labs[labs.length - 1]}
                  onConfirm={(collectionDate, parsedData) => {
                    labs[labs.length - 1].collectionDate = collectionDate;
                    labs[labs.length - 1].analytes = parsedData;
                    setIsCardFrontConfirming(false);
                  }}
                  onFileRemove={() => {
                    const temp = [...labs];
                    temp.pop();
                    setLabs(temp);
                    setIsCardFrontConfirming(false);
                  }}
                />
              </Flex>
            ) : isPdfConfirming ? (
              <Flex flex={1} flexDirection="column" style={{ zIndex: 1 }}>
                <PdfMiniature
                  client={client}
                  file={labs[labs.length - 1]}
                  onConfirm={(collectionDate, parsedData) => {
                    labs[labs.length - 1].collectionDate = collectionDate;
                    labs[labs.length - 1].analytes = parsedData;
                    setIsPdfConfirming(false);
                  }}
                  onFileRemove={() => {
                    const temp = [...labs];
                    temp.pop();
                    setLabs(temp);
                    setIsPdfConfirming(false);
                  }}
                />
              </Flex>
            ) : null}
            {isSending && (
              <ConfirmModal
                isOpen={true}
                childrenManaged
                onClose={() => {}}
                onBackgroundClick={() => {}}
                onEscapeKeydown={() => {}}
                onConfirm={async () => {
                  // wizard.next();
                }}
                confirmLabel="Save"
                cancelLabel="Remove"
                confirmStyle={{
                  width: '100%',
                }}
                cancelStyle={{
                  width: '100%',
                  marginTop: '18px',
                }}
                cancelVariant="outline"
                buttonsDirection="column"
                displayX={false}
                cardStyle={{
                  maxWidth: 'calc(100vw - 40px)',
                }}
              >
                <ModalInner>
                  <Flex flexDirection={'column'} p={3} alignItems={'center'} justifyContent={'center'}>
                    <SpinLoader />
                    <Text mt={3}>Uploading your file</Text>
                    <Text mb={3}>This may take a moment depending on the file size</Text>
                  </Flex>
                </ModalInner>
              </ConfirmModal>
            )}
            {confirmSkip && (
              <ConfirmModal
                isOpen={true}
                childrenManaged
                onClose={() => setConfirmSkip(false)}
                onBackgroundClick={() => setConfirmSkip(false)}
                onEscapeKeydown={() => setConfirmSkip(false)}
                onConfirm={async () => {
                  setConfirmSkip(false);
                  await sleep(100);
                  wizard.next();
                }}
                confirmLabel="Save"
                cancelLabel="Remove"
                confirmStyle={{
                  width: '100%',
                }}
                cancelStyle={{
                  width: '100%',
                  marginTop: '18px',
                }}
                cancelVariant="outline"
                buttonsDirection="column"
                displayX={true}
                cardStyle={{
                  width: '500px',
                  maxWidth: 'calc(100vw - 40px)',
                }}
              >
                {confirmSkip && (
                  <ModalInner>
                    <Heading mb={4} textAlign="center" fontWeight="bold">
                      Without updated labs, medications may not be provided
                    </Heading>
                    <Text mb={3} textAlign="center">
                      {data && data.disclaimer ? data.disclaimer : 'For new patients, labs within four months are required in order to prescribe medication or discuss potential treatment plans.'}
                    </Text>
                    <Button
                      variant={'primary'}
                      style={{ width: '100%' }}
                      onClick={() => {
                        setConfirmSkip(false);
                      }}
                    >
                      Upload Labs
                    </Button>

                    <Button
                      variant={'outline'}
                      mt={2}
                      onClick={async () => {
                        setConfirmSkip(false);
                        await sleep(100);
                        wizard.next();
                      }}
                    >
                      Skip
                    </Button>

                    <Flex style={{ justifyContent: 'center' }}>
                      <Button
                        variant={'pink'}
                        mt={4}
                        onClick={() => {
                          setConfirmSkip(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Flex>
                  </ModalInner>
                )}
              </ConfirmModal>
            )}
          </Fragment>
        );
      }}
    </Query>
  );
};

export default compose(
  withApollo,
  withWizard,
  withToastManager,
)(UploadLabResultsExtended);
